import { DestinationItem, DestinationItemType, NestedProjectExplorerProps, SentinelRootProjectId } from '@tableau/nested-project-explorer';
import React from 'react';
import _ from 'underscore';

import { createMultipleItemResultNotificationValues, IErrorDetail, MultipleItemResultNotificationValues } from '~commonInfra/contentActions/ActionMessages';
import { ContentActionNotification } from '~commonInfra/contentActions/ContentActionNotification';
import { IErrorItem, MultipleItemResult } from '~commonInfra/contentActions/MultipleItemResult';
import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { openReactAlertDialog } from '~commonInfra/dialogs/ReactAlertDialog';
import { openReactConfirmationDialog, ReactConfirmationDialogBodyProps } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '~commonInfra/L10n/L10n';
import { HelpService } from '~commonInfra/services/HelpService';
import { CommonErrorCode, ContentErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Location } from '~commonInfra/utils/Location';
import { Set } from '~commonInfra/utils/Set';
import { SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { IDatabaseInfo } from '~src/catalog/models/IDatabaseInfo';
import { Formatters, Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { IDataSourceBase } from '~src/datasource/models/IDataSourceInfo';
import { DataSourceErrors } from '~src/datasource/services/DataSourceErrors';
import { ContentDraftStatus } from '~src/drafts/serverApi/types/ContentDraft';
import { hasDraftIdPrefix } from '~src/drafts/utils/DraftUtils';
import { FlowBaseInfo } from '~src/flow/models/FlowInfo';
import { FlowErrors } from '~src/flow/services/FlowErrors';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IMultipleContentTypeResult,IResult } from '~src/httpClient/serverApi/types/IResponse';
import { RequestHelper } from '~src/httpClient/utils/RequestHelper';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { selectPersonalSpace } from '~src/personalSpace/selectors/PersonalSpaceSelector';
import { IResultWithDestinationProject } from '~src/projects/actions/MoveToProjectAction';
import { ProjectErrors } from '~src/projects/actions/ProjectErrors';
import { IProjectBase } from '~src/projects/models/IProjectInfo';
import { DestinationProjectMapper } from '~src/projects/models/mappers/DestinationProjectMapper';
import { DestinationProjectContentType, DestinationProjects } from '~src/projects/resources/DestinationProjects';
import { Projects } from '~src/projects/resources/Projects';
import { ContentItem } from '~src/projects/ServerApi/requests/MoveContentToContainerRequest';
import { IDestinationProject } from '~src/projects/ServerApi/types/IDestinationProject';
import { IProjectName } from '~src/projects/ServerApi/types/IProject';
import { IPublishedConnectionInfo } from '~src/publishedConnection/models/IPublishedConnectionInfo';
import { PublishedConnectionErrors } from '~src/publishedConnection/services/PublishedConnectionErrors';
import { RecycleBin } from '~src/recycleBin/resources/RecycleBin';
import { RecycleBinUtils } from '~src/recycleBin/utils/RecycleBinUtils';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { MoveContentDialogBody, MoveContentDialogBodyProps, MoveContentDialogBodyState } from '~src/spacesPlatform/components/content/MoveContentDialogBody';
import { getExplorableItemDefinition } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import {
  DatabaseContentType,
  DatasourceContentType,
  FlowContentType,
  FlowDraftContentType,
  IContentType,
  ProjectContentType,
  PublishedConnectionContentType,
  TableContentType,
  WorkbookContentType} from '~src/spacesPlatform/models/ContentTypes';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { IIdItemInfo, INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { isRecycleBinSpace, SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';
import { IWorkbookBase } from '~src/workbook/models/IWorkbookInfo';
import { WorkbookErrors } from '~src/workbook/services/WorkbookErrors';

import { invalidateProjectQueries, projectQueryKeys } from '../services/Queries';

export namespace MoveContentAction {

  const NoPermission = 'NO_PERMISSION';

  export function confirmMoveContent(content: IContentInfo[]): Promise<IResultWithDestinationProject> {
    const spaceInfo = getAppStore().getState().spaceInfo;

    const projects = Set.fromIdItemArray(content.filter(i => i.contentType === ProjectContentType.name));
    const workbooks = Set.fromIdItemArray(content.filter(i => i.contentType === WorkbookContentType.name));
    const datasources = Set.fromIdItemArray(content.filter(i => i.contentType === DatasourceContentType.name));
    const databases = Set.fromIdItemArray(content.filter(i => i.contentType === DatabaseContentType.name));
    const tables = Set.fromIdItemArray(content.filter(i => i.contentType === TableContentType.name));
    const flows = Set.fromIdItemArray(content.filter(i => i.contentType === FlowContentType.name));
    const publishedConnections = Set.fromIdItemArray(content.filter(i => i.contentType === PublishedConnectionContentType.name));
    const count = content.length;
    let selectedItem: DestinationItem = null;
    let moveDependentContent: boolean = true;
    const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    const personalSpaceStatus = _.some(content, (item) => item.owner && item.owner.id === userId) && getSessionManagementServiceInstance().isPersonalSpaceOnSiteEnabled();

    return initNestedProjectExplorer(projects, workbooks, datasources, databases, flows, publishedConnections, personalSpaceStatus, tables).then((result) => {
      if (result === NoPermission) {
        return alertCantMoveContent(Messages.permissionsLongMessage_cannotMoveItems({ ITEM_COUNT_LOC: Formatters.formatCount(content.length) })).result;
      }

      const actionDisabled = (bodyProps: MoveContentDialogBodyProps, dialogState: MoveContentDialogBodyState) => {
        return dialogState.selectedItem === null;
      };

      const movingDatabases = getSessionManagementServiceInstance().databasesInProjectsPostMVPEnabled() && databases.size() > 0;
      const movingTables = getSessionManagementServiceInstance().tablesInProjectsUIEnabled();
      const dialogInstance = openReactActionDialog<MoveContentDialogBodyProps, MoveContentDialogBodyState, any>({
        title: isRecycleBinSpace(spaceInfo) ? Messages.restoreActionTitle() : (movingDatabases||movingTables ? Messages.moveAssets() : Messages.moveContent()),
        shouldReturnFocusOnClose: true,
        initialBodyProps: {
          prompt: isRecycleBinSpace(spaceInfo) ? getRecycleBinMoveText(content, count, content[0].name, null, 'general') :
            Messages.selectProjectForContent({
            ITEM_COUNT: count,
            ITEM_NAME: L10n.elideIfLong(content[ 0 ].name),
            ITEM_COUNT_LOC: Formatters.formatCount(count)
          }),
          targets: result as NestedProjectExplorerProps,
          movingDatabases: movingDatabases,
          tablesInProjectsUIFeatureEnabled : movingTables,
        },
        initialDialogState: {
          selectedItem: selectedItem,
          moveDependentContent: moveDependentContent
        },
        bodyComponent: MoveContentDialogBody,
        actionName: isRecycleBinSpace(spaceInfo) ? Messages.confirmRestoreAsset() :
          (movingDatabases || movingTables ? Messages.confirmMoveAssets({
          ITEM_COUNT: count,
          ITEM_COUNT_LOC: Formatters.formatCount(count)}) :
            Messages.confirmMoveContent({ITEM_COUNT: count, ITEM_COUNT_LOC: Formatters.formatCount(count)})),
        action: (props: MoveContentDialogBodyProps, state: MoveContentDialogBodyState) => {
          selectedItem = state.selectedItem;
          if (selectedItem.itemType === DestinationItemType.personalSpace) {
            return null;
          }
          let moveDependentContent = movingTables ? state.moveDependentContent : true;
          return moveContent(spaceInfo, projects, workbooks, datasources, databases, tables, flows, publishedConnections, content, state.selectedItem, moveDependentContent);
        },
        actionDisabled,
        isActionDelete: false,
        dismissDialogOnActionFailed: true,
        dialogWidth: TabDialogWidth.width375px
      }) as any;

      return dialogInstance.result.then((result: any) => {
        dialogInstance.dismiss();

        if (selectedItem.itemType === DestinationItemType.personalSpace) {
          const contentItems = getContentItemsList(content, false);
          const contentName = getContentName(workbooks, datasources, contentItems.length);
          const modalInstance = openReactConfirmationDialog({
            title: isRecycleBinSpace(spaceInfo) ? Messages.restoreToPersonalSpaceWarningTitle() : Messages.moveToPersonalSpaceWarningTitle(),
            message: isRecycleBinSpace(spaceInfo) ?
              Messages.restoreToPersonalSpaceWarningPrimaryMessage({
                ITEM_COUNT: contentItems.length,
                CONTENT_TITLE: contentName
              }) :
              Messages.moveToPersonalSpaceWarningPrimaryMessage({
                ITEM_COUNT: contentItems.length,
                CONTENT_TITLE: contentName
              }),
            actionName: isRecycleBinSpace(spaceInfo) ?
              Messages.restoreToPersonalSpaceWarningAction({ITEM_COUNT: contentItems.length}) :
              Messages.moveToPersonalSpaceWarningAction(),
            bodyComponent: getPersonalSpaceWarningDialog,
            action: () => {
              return moveContent(spaceInfo, projects, workbooks, datasources, databases, tables, flows, publishedConnections, content, selectedItem, false);
            },
            dialogWidth: TabDialogWidth.width500px,
            paramsForAction: null,
          });
          return Promise.resolve(modalInstance.result);
        }
        return { ...ResultHelper.combineResults(result), destinationProjectId: getDestinationProjectIdFromDialog(selectedItem) };
      });
    });
  }

  function getRecycleBinMoveText(content: IContentInfo[], count: number, contentName: string,
                                 itemType: ExplorableItemType, context: string) {
    // show single-item name if single item or generic counts if multiple items
    switch (context) {
      case 'general':
        if (count === 1) {
          return Messages.restoreContentToProject({ITEM_NAME: L10n.elideIfLong(content[ 0 ].name)});
        } else {
          return Messages.restoreContentsToProject({
            ITEM_COUNT_LOC: Formatters.formatCount(count),
            CONTENT_TYPE:  L10n.elideIfLong(RecycleBinUtils.getLocalizedContentType(content[0].contentType, count > 1))
          });
        }
      case 'success':
        if (count === 1) {
          return Messages.restoreAssetSuccess({
            CONTENT_NAME: contentName
          });
        } else {
          return Messages.restoreAssetsSuccess({
            ITEM_COUNT_LOC: L10n.localizedCount(count),
            CONTENT_TYPE: L10n.elideIfLong(RecycleBinUtils.getLocalizedContentType(itemType, count > 1))
          });
        }
      case 'error':
        if (count === 1) {
          return Messages.restoreAssetError({
            CONTENT_NAME: contentName
          });
        } else {
          return Messages.restoreAssetsError({
            ERROR_COUNT_LOC: L10n.localizedCount(count),
            CONTENT_TYPE: L10n.elideIfLong(RecycleBinUtils.getLocalizedContentType(itemType, count > 1))
          });
        }
    }
  }

  export function getPersonalSpaceWarningDialog(props: ReactConfirmationDialogBodyProps): JSX.Element {
    return(<div>
              <div>
                {props.message}
              </div>
              <div style ={{marginTop: '20px'}}>
                {Messages.moveToPersonalSpaceWarningSecondaryMessage() + ' '}
                <a href={HelpService.getHelpUrlByCategory('server-personal-move')} target="_blank" rel="noopener noreferrer">{Messages.moveToPersonalSpaceWarningLearnMore()}</a>
              </div>
          </div>);
  }

  function getContentName(workbooks: Set<IContentInfo>, datasources: Set<IContentInfo>, numberOfItems: number) {
    if (numberOfItems === 1 && workbooks.size() === 1) {
      return workbooks.getByIndex(0).name;
    } else if (numberOfItems === 1 && datasources.size() === 1) {
      return datasources.getByIndex(0).name;
    }

    return '';
  }

  function alertCantMoveContent(message: string) {
    return openReactAlertDialog({
      title: Messages.permissions_cannotMoveItems(),
      message
    });
  }

  function getDestinationProjectIdFromDialog(selectedProject: DestinationItem): string {
    const destinationProjectId = selectedProject.id;
    return (destinationProjectId === SentinelRootProjectId ? null : destinationProjectId);
    // API expects a null ID if we're moving a project to the top level of the site
  }

  function initNestedProjectExplorer(
    projects: Set<IContentInfo>,
    workbooks: Set<IContentInfo>,
    datasources: Set<IContentInfo>,
    databases: Set<IContentInfo>,
    flows: Set<IContentInfo>,
    publishedConnections: Set<IContentInfo>,
    personalSpaceStatus: boolean,
    tables: Set<IContentInfo>
  ): Promise<{}>{

    // The Nested Project Explorer presents the user a tree-view showing all the projects on the site,
    // along with their "status" (whether or not the items can actually be moved there).
    // We call the getDestinationProjects API to fetch the information to build this tree, however,
    // due to the limitations of the API, we split the source items by content type as well
    // as in groups of 1000 within each content type, and call the API separately for each of these chunks.
    //
    // The backend returns a complete list of Destination Projects for each chunk of source items
    // (let's call each copy a "slide"). Once we have all the slides, we combine them to create a
    // "projection" of all their statuses to show the user (the user must be able to move all the
    // items to a particular destination in order for it to be valid).
    let slidePromises = [];

    function pluckIds(items: Set<IIdItemInfo>) {
      return items.map(Set.PluckId);
    }

    // flow drafts are not permissioned as a full content type, filter out flow draft ids, the backend will then
    // fall back to user permissions for those ones
    function pluckFlowIds(items: Set<IIdItemInfo>) {
      return items.map(Set.PluckId).filter((id) => {
        return !hasDraftIdPrefix(id);
      });
    }

    function pluckVizportalIds(items: Set<IContentInfo>) {
      return items.map(item => item.vizportalId);
    }

    if (projects.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'project', pluckIds(projects)));
    }
    if (workbooks.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'workbook', pluckIds(workbooks)));
    }
    if (datasources.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'datasource', pluckIds(datasources)));
    }
    if (databases.size() > 0 && FeatureFlags.databasesInProjectsEnabled && FeatureFlags.databasesInProjectsPostMVPEnabled) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'database', pluckVizportalIds(databases)));
    }
    if (tables.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'table', pluckVizportalIds(tables)));
    }
    if (getSessionManagementServiceInstance().flowsOnSiteEnabled() && flows.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'flow', pluckFlowIds(flows)));
    }
    if (publishedConnections.size() > 0) {
      slidePromises.push(...getDestinationProjectTreeSlidePromisesForSourceItems('', 'virtualConnection', pluckIds(publishedConnections)));
    }

    return Promise.all(slidePromises).then((slides: Array<DestinationItem[]>) => {

      // If all the slides are null, it means the user has no permission to move any of the content
      if (slides.every(x => x === null)) {
        return NoPermission;
      }
      let showPersonalSpaceOption =  isPersonalSpaceAvailable();
      personalSpaceStatus = personalSpaceStatus && isPersonalSpaceSupported(workbooks);
      return {
        topLevelDestinationStatus: getTopLevelDestinationStatus(datasources, databases, flows, workbooks, publishedConnections),
        projects: makeDestinationItemSlideProjection(slides, showPersonalSpaceOption, personalSpaceStatus)
      };
    });
  }

  export function isPersonalSpaceSupported(...contentArgs: Set<IContentInfo>[]): boolean {
    return contentArgs.some((content) => isPersonalSpaceSupportedForContent(content));
  }

  function isPersonalSpaceSupportedForContent(content: Set<IContentInfo>): boolean {
    if (content.isEmpty()) {
      return false;
    }
    const contentType = content.first().contentType;
    const supportedContentTypes = [ WorkbookContentType.name ];

    return supportedContentTypes.includes(contentType);
  }

  export function isPersonalSpaceAvailable(): boolean {
    const personalSpaceInfo = selectPersonalSpace(getAppStore().getState());
    if (!getSessionManagementServiceInstance().isPersonalSpaceOnSiteEnabled() || personalSpaceInfo.readOnly) {
      return false;
    }

    return true;
  }

  function addPersonalSpaceToProjection(projection: DestinationItem[], personalSpaceStatus: boolean) {
    const personalSpaceInfo = selectPersonalSpace(getAppStore().getState());
    let personalSpace: DestinationItem = {
      id: personalSpaceInfo.luid,
      name: '', // This is not used for PS but is a required field so have to be set
      itemType: DestinationItemType.personalSpace,
      childItemCount: 0,
      topLevelItem: true,
      status: personalSpaceStatus ? 'valid' : 'read_only',
    };
    return projection.concat(personalSpace);
  }

  //export for unit testing
  export function makeDestinationItemSlideProjection(slides: Array<DestinationItem[]>, showPersonalSpace: boolean, personalSpaceStatus: boolean) {
    let projection: DestinationItem[] = null;
    slides.forEach((slide) => {
      if (slide === null) {
        return;
      } else if (projection === null) {
        projection = slide;
      } else {
        projection = addSlideToProjection(slide, projection);
      }
    });
    if (showPersonalSpace) {
      projection = addPersonalSpaceToProjection(projection, personalSpaceStatus);
    }

    return projection;
  }

  function addSlideToProjection(slide: DestinationItem[], projection: DestinationItem[]) {
    slide.forEach((node: DestinationItem) => {
      const itemAlreadyAdded = projection.find(item => item.id === node.id);
      if (!itemAlreadyAdded) {
        projection.push(node);
      } else if (node.status !== 'valid') {
        itemAlreadyAdded.status = node.status;
      }
    });
    return projection;
  }

  // export for unit testing
  export function getTopLevelDestinationStatus(
    datasources: Set<IContentInfo>,
    databases: Set<IContentInfo>,
    flows: Set<IContentInfo>,
    workbooks: Set<IContentInfo>,
    publishedConnections: Set<IContentInfo>,
  ) {
    if (workbooks.size() > 0 || datasources.size() > 0 || databases.size() > 0 || flows.size() > 0 ||
        publishedConnections.size() > 0) {
      return 'structurally_invalid';
    } else if (!getSessionManagementServiceInstance().isSessionSiteOrServerAdmin()) {
      return 'insufficient_permissions';
    } else {
      return 'valid';
    }
  }

  function getDestinationProjectTreeSlidePromisesForSourceItems(
    query: string,
    contentType: DestinationProjectContentType,
    itemIds: string[],
  ): Promise<DestinationItem[]>[] {

    // We can send only 1000 IDs to the API at a time, so make chunks of 1000 IDs and do the queries in parallel.
    const idBlocks = RequestHelper.makeBlocks(itemIds, RequestHelper.DefaultBlockSize);
    return idBlocks.map(idBlock => {
      // Ideally this query could be replaced by the newer implementation in `ProjectTreeService`
      const projectsQuery = DestinationProjects.getQueryForSpecificSourceContentAndMatchText(
        contentType, idBlock, query);

      return SliceableHelper.fetchAll(projectsQuery).then(createDestinationProjectTreeSlide);
    });
  }

  function createDestinationProjectTreeSlide(projects: ListResult<IDestinationProject>): DestinationItem[] {
    // Use the result of getDestinationProjects to infer if the user can move the items.
    // If no items are returned, the user doesn't have permission to move some of the selected items.
    // The check needs to be items[0] being undefined as the response that comes back is actually an array with first item as undefined.
    if (projects.items[0] === undefined) {
      return null;
    }

    const destinationProjects = DestinationProjectMapper.map(projects.items);
    return destinationProjects;
  }

  //export for unit testing
  export function moveContent(
    spaceInfo: SpaceInfo,
    projects: Set<IContentInfo>,
    workbooks: Set<IContentInfo>,
    databases: Set<IContentInfo>,
    tables: Set<IContentInfo>,
    datasources: Set<IContentInfo>,
    flows: Set<IContentInfo>,
    publishedConnections: Set<IContentInfo>,
    content: IContentInfo[],
    selectedItem: DestinationItem,
    moveDependentContent: boolean,
  ) {
    const destinationProjectId = getDestinationProjectIdFromDialog(selectedItem);
    let moveFunctions: any[] = [];
    if(selectedItem.itemType === DestinationItemType.personalSpace) {

        if(RecycleBinUtils.isRecycleBinEnabled() && isRecycleBinSpace(spaceInfo)){
          moveFunctions.push(restoreContentFromRecycleBin(
            selectedItem.id,
            selectedItem.itemType,
            getContentItemsList(content, moveDependentContent),
            content,
            spaceInfo,
            selectedItem.name.length === 0 ? undefined: selectedItem.name));
        } else {
          moveFunctions.push(moveContentToContainer(
            selectedItem.id,
            selectedItem.itemType,
            getContentItemsList(content, moveDependentContent),
            content,
            spaceInfo,
            selectedItem.name.length === 0 ? undefined: selectedItem.name));
        }
    } else {
      let allContent: IContentInfo[] = getContentForMove(content);
      if (allContent.length > 0) {
        if(RecycleBinUtils.isRecycleBinEnabled() && isRecycleBinSpace(spaceInfo)){
          moveFunctions.push(restoreContentFromRecycleBin(
            selectedItem.id,
            selectedItem.itemType,
            getContentItemsList(content, moveDependentContent),
            content,
            spaceInfo,
            selectedItem.name.length === 0 ? undefined: selectedItem.name));
        } else {
          moveFunctions.push(moveContentToContainer(
            selectedItem.id,
            selectedItem.itemType,
            getContentItemsList(allContent, moveDependentContent),
            allContent,
            spaceInfo,
            selectedItem.name.length === 0 ? undefined: selectedItem.name));
        }
      }
    }
    return Promise.all(moveFunctions);
  }

  // This method is to collect all the content items to be used in with the new moveContentToContainer API
  // This method will go away once all the content types start using the new API
  function getContentForMove(content: IContentInfo[]) {

    let workbookAndDataSources: IContentInfo[] = content.filter(c => {
      return (c.contentType === WorkbookContentType.name || c.contentType === DatasourceContentType.name);
    });

    let databases: IContentInfo[] = content.filter(c=> {
      return (c.contentType === DatabaseContentType.name);
    });

    let tables: IContentInfo[] = content.filter(c=> {
      return (c.contentType === TableContentType.name);
    });

    let flows: IContentInfo[] = [];
    if (getSessionManagementServiceInstance().flowsOnSiteEnabled()) {
      flows = content.filter(c => {
        return c.contentType === FlowContentType.name && c.draftStatus !== ContentDraftStatus.unpublished;
      });
    }

    let flowDrafts: IContentInfo[] = [];
    if (getSessionManagementServiceInstance().flowsOnSiteEnabled()) {
      flowDrafts = content.filter(c => {
        return c.contentType === FlowContentType.name && c.draftStatus === ContentDraftStatus.unpublished;
      });
    }

    let virtualConnections: IContentInfo[] = content.filter(c => {
      return (c.contentType === PublishedConnectionContentType.name);
    });

    let projects: IContentInfo[] = content.filter(c => {
      return (c.contentType === ProjectContentType.name);
    });

    return [...workbookAndDataSources, ...databases, ...tables, ...flows, ...flowDrafts, ...projects, ...virtualConnections];
  }

  function getContentItemsList(content: IContentInfo[], moveDependentContent: boolean): ContentItem[] {
    let contentItems: ContentItem[] = [];
    if (content.length > 0) {
      contentItems.push(...content.map(content => ({
        contentId: ((content.contentType === DatabaseContentType.name) || (content.contentType === TableContentType.name)) ? content.vizportalId :
                    (content.contentType === FlowContentType.name && content.draftStatus === ContentDraftStatus.unpublished) ? content.draftId :
                    content.id,
        contentType: (content.contentType === FlowContentType.name && content.draftStatus === ContentDraftStatus.unpublished) ? FlowDraftContentType.name : content.contentType,
        moveType: content.contentType === DatabaseContentType.name && moveDependentContent ? 'withChildContentItems' : null,
      })));
    }
    return contentItems;
  }

  const allContentTypes = [
    ProjectContentType,
    WorkbookContentType,
    DatasourceContentType,
    FlowContentType,
    PublishedConnectionContentType,
    ... FeatureFlags.databasesInProjectsEnabled && FeatureFlags.databasesInProjectsPostMVPEnabled ? [DatabaseContentType] : [],
    TableContentType
  ];

  function moveContentToContainer(containerId: string, containerType: string, contentItems: ContentItem[], contentItemInfos: IContentInfo[], spaceInfo: SpaceInfo, projectName?: string): Promise<IMultipleContentTypeResult> {
    return Projects.moveToContainer(containerId, containerType, contentItems)
      .then((result: IMultipleContentTypeResult) => {
        const contentTypeToErrors = _.groupBy(result.errors, error => error.contentType);
        allContentTypes.forEach((contentType: IContentType) => {
          const contentTypeResult: IResult = {errors: contentTypeToErrors[contentType.name]};
          let contentTypeItems;
          let flowDraftContentTypeItems;
          if (contentType.name === DatabaseContentType.name) {
            contentTypeItems = new Set((item: IContentInfo) => item.vizportalId);
            contentTypeItems.addItems(contentItemInfos.filter(i => i.contentType === contentType.name));
          } else if (contentType.name === FlowContentType.name) {
            contentTypeItems = Set.fromIdItemArray(contentItemInfos.filter((i, index) => (i.contentType === contentType.name && i.draftStatus !== ContentDraftStatus.unpublished)));
            flowDraftContentTypeItems = Set.fromIdItemArray(contentItemInfos.filter((i, index) => (i.contentType === contentType.name && i.draftStatus === ContentDraftStatus.unpublished)));
          } else {
            contentTypeItems = Set.fromIdItemArray(contentItemInfos.filter(i => i.contentType === contentType.name));
          }
          if (contentTypeItems.size() > 0) {
            ContentActionNotification.displayItemizedToastsForResult(
              notificationForMoveContentToContainer(
              new MultipleItemResult(contentTypeItems, contentTypeResult),
              contentType.name as ExplorableItemType,
              containerType as DestinationItemType,
              spaceInfo,
              projectName,
              containerId
            ));
          }
          if (flowDraftContentTypeItems?.size() > 0) {
            const project: IProjectName = {id: containerId, name: projectName};
            ContentActionNotification.displayItemizedToastsForResult(
              notification(
                new MultipleItemResult(flowDraftContentTypeItems, contentTypeResult),
                Messages.moveFlowDraftsToProjectSuccess,
                Messages.moveFlowDraftsToProjectError,
                FlowContentType,
                project
              )
            );
          }
        });
        if (contentItems.some(i => i.contentType === ProjectContentType.name)) {
          invalidateProjectQueries(projectQueryKeys.all());
        }
        return result;
      });
  }

  function restoreContentFromRecycleBin(containerId: string,
                                        containerType: string,
                                        contentItems: ContentItem[],
                                        contentItemInfos: IContentInfo[],
                                        spaceInfo: SpaceInfo,
                                        projectName?: string): Promise<IMultipleContentTypeResult> {
    return RecycleBin.restoreContentToContainer(containerId, containerType, contentItems)
      .then((result: IMultipleContentTypeResult) => {
        const contentTypeToErrors = _.groupBy(result.errors, error => error.contentType);
        allContentTypes.forEach((contentType: IContentType) => {
          const contentTypeResult: IResult = {errors: contentTypeToErrors[contentType.name]};

          const contentTypeItems = Set.fromIdItemArray(contentItemInfos.filter(i => i.contentType === contentType.name));

          if (contentTypeItems.size() > 0) {
            ContentActionNotification.displayItemizedToastsForResult(
              notificationForMoveContentToContainer(
                new MultipleItemResult(contentTypeItems, contentTypeResult),
                contentType.name as ExplorableItemType,
                containerType as DestinationItemType,
                spaceInfo,
                projectName,
                containerId
              ));
          }
        });
        if (contentItems.some(i => i.contentType === ProjectContentType.name)) {
          invalidateProjectQueries(projectQueryKeys.all());
        }
        return result;
      });
  }

  function notification(
    result: MultipleItemResult<INamedItemInfo>,
    successId: (a: {
      ITEM_COUNT: number;
      ITEM_COUNT_LOC: string;
      PROJECT_NAME: string;
    }) => string,
    errorId: (a: {
      ERROR_COUNT: number;
      ERROR_COUNT_LOC: string;
      PROJECT_NAME: string;
    }) => string,
    itemType: IContentType,
    project: IProjectName): MultipleItemResultNotificationValues {

    return createMultipleItemResultNotificationValues(result,
      {
        describeSuccessSummary: (successCount: number) =>
          successId({
            ITEM_COUNT: successCount,
            PROJECT_NAME: L10n.elideIfLong(project.name),
            ITEM_COUNT_LOC: Formatters.formatCount(successCount)
          }),

        describeErrorSummary: (errorCount: number) =>
          errorId({
            ERROR_COUNT: errorCount,
            PROJECT_NAME: L10n.elideIfLong(project.name),
            ERROR_COUNT_LOC: Formatters.formatCount(errorCount)
          }),

        describeErrorDetail: (errorItem: IErrorItem<INamedItemInfo>) => {
          if (!errorItem.item || !errorItem.error) return;
          if (itemType === ProjectContentType) {
            return ProjectErrors.errorDetailsFor(errorItem as IErrorItem<IProjectBase>);
          } else if (itemType === DatasourceContentType) {
            return DataSourceErrors.errorDetailsFor(errorItem as IErrorItem<IDataSourceBase>);
          } else if (itemType === DatabaseContentType) {
            return moveContentToContainerErrorMessageDetail(errorItem as IErrorItem<IDatabaseInfo>, 'database');
          } else if (itemType === WorkbookContentType) {
            return WorkbookErrors.errorDetailsFor(errorItem as IErrorItem<IWorkbookBase>);
          } else if (itemType === FlowContentType) {
            return FlowErrors.errorDetailsFor(errorItem as IErrorItem<FlowBaseInfo>);
          } else if (itemType === PublishedConnectionContentType) {
            return PublishedConnectionErrors.errorDetailsFor(errorItem as IErrorItem<IPublishedConnectionInfo>);
          }
        }
      }
    );
  }

  function notificationForMoveContentToContainer(
    result: MultipleItemResult<INamedItemInfo>,
    itemTypeName: ExplorableItemType,
    destinationType: DestinationItemType,
    spaceInfo: SpaceInfo,
    projectName?: string,
    projectId?: string): MultipleItemResultNotificationValues {

    return createMultipleItemResultNotificationValues(result,
      {
        describeSuccessSummary: (successCount: number) =>
          getSuccessMessageForMoveContentToContainer(destinationType, itemTypeName, successCount, spaceInfo,
            result.firstSuccessfulItem().name, projectName),

        successActionMessage: (successCount: number) => {
          switch (destinationType) {
            case DestinationItemType.personalSpace:
              return Messages.goToPersonalSpace();
            case DestinationItemType.project:
              return Messages.goToProject();
          }
        },

        describeErrorSummary: (errorCount: number) =>
          getErrorMessageForMoveContentToContainer(destinationType, itemTypeName, errorCount, spaceInfo,
            result.firstErrorItem().name, projectName),

        describeErrorDetail: (errorItem: IErrorItem<INamedItemInfo>) => {
          if (!errorItem.item || !errorItem.error) return;
          if (itemTypeName === ProjectContentType.name) {
            return ProjectErrors.errorDetailsFor(errorItem as IErrorItem<IProjectBase>);
          } else if (itemTypeName === DatasourceContentType.name) {
            return DataSourceErrors.moveContentToContainerErrorMessageDetailsFor(errorItem as IErrorItem<IDataSourceBase>, destinationType);
          } else if (itemTypeName === WorkbookContentType.name) {
            return WorkbookErrors.moveContentToContainerErrorMessageDetailsFor(errorItem as IErrorItem<IWorkbookBase>, destinationType);
          } else if (itemTypeName === FlowContentType.name) {
            return FlowErrors.errorDetailsFor(errorItem as IErrorItem<FlowBaseInfo>, destinationType);
          } else if (itemTypeName === PublishedConnectionContentType.name) {
            return PublishedConnectionErrors.errorDetailsFor(errorItem as IErrorItem<IPublishedConnectionInfo>);
          } else {
            return moveContentToContainerErrorMessageDetail(errorItem, itemTypeName);
          }
        }
      },
      successAction(destinationType, projectId),
    );
  }

  function moveContentToContainerErrorMessageDetail(errorItem: IErrorItem<INamedItemInfo>, itemTypeName: ExplorableItemType): IErrorDetail {
    return {
      itemName: errorItem.item.name,
      itemUrl: null,
      errorMessage: moveContentToContainerErrorMessageForContent(errorItem.error, itemTypeName)
    };
  }

  function moveContentToContainerErrorMessageForContent(error: IError, itemTypeName: ExplorableItemType): string {
    switch(error.code) {
      case ContentErrorCode.INVALID_CONTENT_TYPE:
      case CommonErrorCode.UnexpectedParameterValue: // projects
        return Messages.moveContentToPersonalSpaceErrorDetail({CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemTypeName, true))});
      default:
        return Messages.errorUnknown();
    }
  }

  function successAction(containerType: DestinationItemType, projectId?: string) {
    return () => {
      const siteUrls = siteUrlsSelector(getAppStore().getState());
      switch(containerType) {
        // TODO: For Recycle Bin, product requested to see if we could direct the users directly
        // to the item rather than to the space for item restore [GUS: @W-16519621]
        case DestinationItemType.personalSpace:
          Location.instance.setUrl(siteUrls.personalSpaceUrl());
          break;
        case DestinationItemType.project:
          Location.instance.setUrl(siteUrls.projectUrl(projectId));
          break;
        default:
          break;
      }
    };
  }

  function getSuccessMessageForMoveContentToContainer(containerType: DestinationItemType, itemType: ExplorableItemType,
                                                      count: number, spaceInfo: SpaceInfo, contentName: string,
                                                      projectName?: string) {
    if (isRecycleBinSpace(spaceInfo)) {
      return getRecycleBinMoveText(null, count, contentName, itemType, 'success');
    } else if (containerType === DestinationItemType.personalSpace) {
        return Messages.moveContentToPersonalSpaceSuccess({
          ITEM_COUNT: L10n.localizedCount(count),
          CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemType, count > 1))
    });
    } else {
        // show single project name if single project or generic counts if multiple projects
        if (count === 1) {
          return Messages.moveContentToProjectSuccess({
            SOURCE_PROJECT_NAME: L10n.elideIfLong(contentName),
            PROJECT_NAME: L10n.elideIfLong(projectName)
          });
        } else {
          return Messages.moveContentsToProjectSuccess({
            ITEM_COUNT: L10n.localizedCount(count),
            CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemType, count > 1)),
            PROJECT_NAME: projectName
          });
        }
    }
  }

  function getErrorMessageForMoveContentToContainer(containerType: DestinationItemType, itemTypeName: ExplorableItemType,
                                                    count: number, spaceInfo: SpaceInfo, contentName: string,
                                                    projectName?: string) {
    if (isRecycleBinSpace(spaceInfo)) {
      return getRecycleBinMoveText(null, count, contentName, itemTypeName, 'error');
    } else if (containerType === DestinationItemType.personalSpace) {
        return Messages.moveContentToPersonalSpaceError({
          ERROR_COUNT: L10n.localizedCount(count),
          CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemTypeName, count > 1))
    });
    } else {
        return Messages.moveContentToProjectError({
          ERROR_COUNT: L10n.localizedCount(count),
          CONTENT_TYPE: L10n.elideIfLong(getLocalizedContentType(itemTypeName, count > 1)),
          PROJECT_NAME: projectName
    });
    }
  }

  function getLocalizedContentType(contentType: ExplorableItemType, isPlural: boolean): string {
    const typeDefinition = getExplorableItemDefinition(contentType);
    if (_.isUndefined(typeDefinition)) {
      throw new Error('Tried to localize unrecognized content type string: ' + contentType);
    }
    return isPlural ? typeDefinition.pluralName() : typeDefinition.singularName();
  }
}

export type MoveContentAction = typeof MoveContentAction;
